import React, { useEffect, useMemo, useState } from 'react';
import i18next from 'i18next';
import { useSelector } from 'react-redux';
import {
  formatApplicationStatus,
  formatNightDayTable,
} from 'utils/formatFieldUtils';

import TableCustom from 'components/common/TableCustom';
import { Link } from 'react-router-dom';
import { useGetApplicationsQuery } from 'redux/@rtkQuery/contacts';
import { getCentreId } from 'redux/auth/selectors';
import { DEFAULT_CURRENT_PAGE } from 'containers/MarketingMetrics/constants';
import { SORT_DESCEND_VALUE } from 'configs/localData/constant';
import { DefaultFilter } from 'redux/@rtkQuery/contacts/types';
import { useParams } from 'react-router';
import {
  DEFAULT_LIMIT_ITEM,
  PAGE_SIZE_OPTIONS,
} from 'containers/Contacts/constants';
import TagCustom from 'components/common/TagCustom';
import GroupActionApplication from './GroupActionApplication';
import CustomCentreName from '../CustomCentreName';

const DEFAULT_SORT_VALUE = '-submittedAt';

const DEFAULT_FILTER: DefaultFilter = {
  sorts: [DEFAULT_SORT_VALUE],
  size: DEFAULT_LIMIT_ITEM,
  page: DEFAULT_CURRENT_PAGE,
};
const ApplicationsTable = () => {
  const centreId = useSelector(getCentreId);
  const [filterValue, setFilterValue] = useState(DEFAULT_FILTER);

  const { id } = useParams();

  const { data, isLoading, refetch, isFetching } = useGetApplicationsQuery({
    contactId: id,
    ...filterValue,
  });

  const columns = useMemo(
    () => [
      {
        title: i18next.t('centres.centreName'),
        dataIndex: 'centreName',
        key: 'centreName',
        sorter: true,
        width: '30%',
        render: (_, record) => <CustomCentreName record={record} />,
        fixed: 'left',
      },
      {
        title: i18next.t('applications.appliedFor'),
        dataIndex: 'appliedFor',
        key: 'appliedFor',
        render: (_, record) => (
          <Link to={`/applications/${record.id}/show`}>
            {`${record?.childFirstName || ''} ${record.childLastName || ''}`}
          </Link>
        ),
        width: 250,
        sorter: true,
      },
      {
        title: i18next.t('applications.status'),
        dataIndex: 'status',
        key: 'status',
        render: formatApplicationStatus,
        width: 200,
      },
      {
        title: i18next.t('applications.appliedAt'),
        dataIndex: 'submittedAt',
        key: 'submittedAt',
        render: (data) => formatNightDayTable(data),
        width: 250,
        sorter: true,
      },
      {
        title: i18next.t('input.brand.name'),
        dataIndex: 'brandName',
        key: 'brandName',
        sorter: true,
        width: 250,
        render: (value) => value || '-',
      },
      {
        title: i18next.t('centres.subscription'),
        dataIndex: 'subscriptionRank',
        key: 'subscriptionRank',
        sorter: true,
        width: 200,
        render: (_, record) => (
          <TagCustom
            text={record?.subscription?.name}
            backgroundColor={record?.subscription?.backgroundColor}
            color={record?.subscription?.textColor}
          />
        ),
      },
      {
        fixed: 'right',
        width: 80,
        dataIndex: 'id',
        key: 'id',
        render: (id, record) => (
          <GroupActionApplication
            id={id}
            packageName={record?.subscription?.name}
            centreId={record?.centreId}
          />
        ),
      },
    ],
    [],
  );

  useEffect(() => {
    refetch();
  }, [centreId, refetch]);

  const onChange = (pagination, filters, sorter) => {
    const formatSort =
      sorter && sorter.columnKey && sorter.order
        ? `${sorter.order === SORT_DESCEND_VALUE ? '-' : ''}${sorter.columnKey}`
        : null;

    setFilterValue({
      sorts: [formatSort || DEFAULT_SORT_VALUE],
      size: pagination?.pageSize,
      page: pagination?.current,
    });
  };

  return (
    <TableCustom
      pagination={{
        pageSize: data?.paging?.size || DEFAULT_LIMIT_ITEM,
        current: data?.paging?.page || DEFAULT_CURRENT_PAGE,
        total: data?.paging?.total || 0,
        pageSizeOptions: PAGE_SIZE_OPTIONS,
      }}
      isResetStyle
      xScroll={1600}
      data={data?.results || []}
      columns={columns}
      loading={isLoading || isFetching}
      onChange={onChange}
    />
  );
};

export default ApplicationsTable;
